/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { GalleryMode, maxRows } from './galleryTypes';
import { askChatTiles, knowledge3MTiles, signalsTiles, legalTiles, devOpsTiles, reportingPortalTiles, tebgTiles, ppmoTiles, travelExpensesTiles, itsmTiles, jiraTiles, hrTiles, allModes } from './galleryData';
import { GalleryTileProps } from '../DefaultPromptTile';

/**
 * Get the number of tiles for a specific gallery mode
 */
export function getTilesForMode(mode: GalleryMode | null): number {
  switch (mode) {
    case 'Ask Chat':
      return askChatTiles.try.length;
    case '3M Knowledge':
      return knowledge3MTiles.try.length;
    case '3M Signals':
      return signalsTiles.try.length;
    case 'Legal':
      return legalTiles.try.length;
    case 'DevOps':
      return devOpsTiles.try.length;
    case 'Reporting Portal':
      return reportingPortalTiles.try.length;
    case 'TEBG':
      return tebgTiles.try.length;
    case 'PPMO':
      return ppmoTiles.try.length;
    case 'Travel & Expenses':
      return travelExpensesTiles.try.length;
    case 'ITSM':
      return itsmTiles.try.length;
    case 'JIRA':
      return jiraTiles.try.length;
    case 'HR':
      return hrTiles.try.length;
    default:
      return 0;
  }
}

/**
 * Get the tile data array for a specific gallery mode
 */
export function getTileDataForMode(mode: GalleryMode | null): {
    try: {
        title: string;
        subtitle: string;
    }[];
    new: GalleryTileProps[];
} {
  switch (mode) {
    case 'Ask Chat':
      return askChatTiles;
    case '3M Knowledge':
      return knowledge3MTiles;
    case '3M Signals':
      return signalsTiles;
    case 'Legal':
      return legalTiles;
    case 'DevOps':
      return devOpsTiles;
    case 'Reporting Portal':
      return reportingPortalTiles;
    case 'TEBG':
      return tebgTiles;
    case 'PPMO':
      return ppmoTiles;
    case 'Travel & Expenses':
      return travelExpensesTiles;
    case 'ITSM':
      return itsmTiles;
    case 'JIRA':
      return jiraTiles;
    case 'HR':
      return hrTiles;
    default:
      return { try: [], new: [] };
  }
}

/**
 * Calculate grid columns for a given number of tiles and mode
 * In the future we could customize this per mode if needed
 */
export function calculateGridColumns(currentTiles: number): number {
  let gridColumns = Math.ceil(currentTiles / maxRows);
  return gridColumns;
}

/**
 * Calculate responsive tile size constraints based on window width
 */
export function getResponsiveTileConstraints(windowWidth: number) {
  let minTileWidth, maxTileWidth;
  
  if (windowWidth < 480) { // Very small screens
    minTileWidth = 40; // Allow even smaller tiles on very small screens
    maxTileWidth = 80; // Much smaller cap for very small screens
  } else if (windowWidth < 768) {
    minTileWidth = 50; // Allow much smaller tiles on mobile
    maxTileWidth = 120; // Cap tiles smaller on mobile
  } else {
    minTileWidth = 70;
    maxTileWidth = 220;
  }
  
  return { minTileWidth, maxTileWidth };
}

/**
 * Calculate responsive container padding based on window width
 */
export function getResponsiveContainerPadding(windowWidth: number): number {
  if (windowWidth < 480) { // Very small screens
    return 20; // Minimal padding on very small screens
  } else if (windowWidth < 768) {
    return 40; // Much smaller padding on mobile
  } else {
    return 96; // Normal padding for larger screens
  }
}

/**
 * Calculate responsive available width percentage based on window width
 */
export function getResponsiveAvailableWidthPercent(windowWidth: number): number {
  if (windowWidth < 480) { // Very small screens
    return 0.9; // Use most of the width on very small screens
  } else if (windowWidth < 768) { // Small screens
    return 0.7; // Use much less width to create natural padding
  } else if (windowWidth < 1200) { // Medium screens
    return 0.85; // Create some space
  } else { // Large screens
    return 0.95; // Still leave some space
  }
}

/**
 * Calculate responsive max parent height based on window dimensions, chat input height, and current mode tile count
 */
export function getResponsiveMaxParentHeight(windowWidth: number, windowHeight: number, chatInputHeight: number = 56, currentTileCount: number = 7): number {
  // Exact calculation based on actual elements:
  // Available height = windowHeight - all the things that take up space
  
  const headerHeight = 50; // main page header at top
  const mainContentTopMargin = 0; // 10vh/6vh is handled separately, don't double-count
  const greetingHeight = windowWidth < 1400 ? 32 : 40; // Reduced at zoom
  const spaceBetweenGreetingAndChat = windowWidth < 1400 ? 8 : 8; // Consistent minimal space
  const spaceBetweenChatAndGallery = 32; // mt-8 = 32px (more space here as requested)
  const titleHeight = 30; // Height for "Give it a Try!" title
  const galleryBottomMargin = 8; // mb-2 = 8px space between gallery and legal
  const disclaimerHeight = 10; // Minimal disclaimer height (just enough for the text)
  const disclaimerBottomPadding = 0; // No extra padding needed
  
  // Don't include gapBetweenTiles here - that's handled in the tile height calculation
  const fixedOverhead = headerHeight + mainContentTopMargin + greetingHeight + spaceBetweenGreetingAndChat + 
                       spaceBetweenChatAndGallery + titleHeight + galleryBottomMargin + disclaimerHeight + disclaimerBottomPadding;
  const availableForTiles = windowHeight - chatInputHeight - fixedOverhead;
  
  return Math.max(100, availableForTiles); // Ensure minimum viable height
}

/**
 * Get the maximum number of tiles across all modes
 */
export function getMaxTiles(): number {
  return Math.max(...allModes.map((arr: GalleryTileProps[]) => arr.length));
}